import { create } from "zustand";
import type { ItemsToSet } from "@/services/entity-instance.service";

export interface EntityInstanceModalView {
  key: string;
  /**
   * Unique identifier for this view (e.g. `${entityName}-${id}`)
   */
  entityName: string;
  entityInstanceId?: string;
  entityFromListCache?: object;
  fieldsToHide?: Set<string>;
  /** Create mode only: scalar field values the empty form starts out with. */
  initialValues?: Record<string, unknown>;
  /** Create mode only: relations to pre-select, keyed by field name. */
  initialRelations?: ItemsToSet;
  /**
   * Callback when entity is created or updated in this view
   */
  onSave?: ({
    dontTriggerFlows,
    data,
  }: {
    dontTriggerFlows: boolean;
    data: { [field: string]: unknown };
  }) => Promise<unknown>;
}

interface EntityInstanceModalStackStore {
  stack: EntityInstanceModalView[];
  push: (view: EntityInstanceModalView) => void;
  pop: () => void;
  /** Truncate the stack so `index` becomes the top. A negative index empties it. */
  popTo: (index: number) => void;
  reset: () => void;
  replace: (view: EntityInstanceModalView) => void; // Replace current top view
}

export const useEntityInstanceModalStack = create<EntityInstanceModalStackStore>((set, get) => ({
  stack: [],

  push: (view: EntityInstanceModalView) => {
    const currentStack = get().stack;
    set({ stack: [...currentStack, view] });
  },

  pop: () => {
    const currentStack = get().stack;
    if (currentStack.length === 0) return;

    set({ stack: currentStack.slice(0, -1) });
  },

  popTo: (index: number) => {
    set({ stack: get().stack.slice(0, Math.max(0, index + 1)) });
  },

  reset: () => {
    set({ stack: [] });
  },

  replace: (view: EntityInstanceModalView) => {
    const currentStack = get().stack;
    if (currentStack.length === 0) {
      set({ stack: [view] });
    } else {
      set({ stack: [...currentStack.slice(0, -1), view] });
    }
  },
}));
